import type React from "react";
import { useState } from "react";
import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Fab,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import FundraiserCard from "./components/FundraiserCard";
import StreamHeader from "./view-stream/StreamHeader";
import EventDetailsCard from "./view-stream/EventDetailsCard";
import TimeAllocationCard from "./view-stream/TimeAllocationCard";
import CrewAssignmentTable from "./view-stream/CrewAssignmentTable";
import type { RootState } from "../../store/Index";
import { useStreamData } from "./hooks/useStreamData";
import formatDate, { formatDateToLongString } from "../../utils/FormatDate";
import DynamicThumbnailCard from "./components/thumbnail/ThumbnailCard";
import NetworkingStatisticsCard from "./view-stream/NetworkingStatistics";
import NotesCard from "./components/NotesCard";
import YouTubeModal from "./components/youtube-modal/YoutubeModal";
import AddNetworkingStatisticsModal from "./components/Networking";

import ThumbnailPreviewDialog from "./components/thumbnail/Thumbnail";
import { motion } from "framer-motion";
import SelectYouTubeChannelModal from "./components/youtube-modal/SelectYoutubeChannel";
import muzukuruChannellogo from "./../../assets/Muzukuruimg3.png";
import livestreamingChannellogo from "./../../assets/livestreaminglogo.png";
import SelectTemplateModal from "./components/share-message/SelectTemplateModal";
import { ShareTemplate } from "../configurations/share-messages/types/share-template.types";
import SocialShareModal from "./components/share-message/ShareMessage";
import StreamProgramCard from "./components/StreamProgram";
import MemoryBookAssignment from "./view-stream/MemoryBookAssignment";
import ServiceDeliveryNoteCard from "./components/ServiceDeliveryNoteCard";
import { Close, Download, ReceiptLongOutlined } from "@mui/icons-material";
import MemorialGraphicCard from "./components/share-message/ShareMessageThumbnailCard";
import { formatTime } from "../../utils/FormatTime";
import sharemessagegraphicpattern from "./components/share-message/pattern.png";
import muzukurulogo from "../../assets/muzukurublack.png";
import { formatVenueName } from "../../utils/formatVenueDisplay";
import CostAnalysis from "../cost-analysis/CostAnalysis";

const ViewStream: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const {
    stream,
    crewMembersData,
    isLoading,
    error,
    timeData,
    networkData,
    fetchData,
    fetchNetworkData,
  } = useStreamData(id);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<
    "facebook" | "whatsapp"
  >("facebook");
  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);
  const [thumbnailType, setThumbnailType] = useState<string | null>(null);
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
  const [costAnalysisOpen, setCostAnalysisOpen] = useState(false);
  const [channelSelectOpen, setChannelSelectOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<{
    name?: string;
  } | null>(null);

  const [selectTemplateOpen, setSelectTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ShareTemplate | null>(null);
  const [downloadFn, setDownloadFn] = useState<null | (() => void)>(null);

  const channels = [
    {
      id: "1",
      name: "Muzukuru Funeral Streaming Services",
      logo: muzukuruChannellogo,
      email: "@Muzukuru",
    },
    {
      id: "2",
      name: "The Livestreaming Company",
      logo: livestreamingChannellogo,
      email: "@The Livestreaming Company",
    },
  ];

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  const [thumbnailRefreshTrigger, setThumbnailRefreshTrigger] = useState(0);

  const streamId = id ? Number.parseInt(id) : undefined;

  const handleOpenThumbnailModal = () => {
    setThumbnailType("youtube_thumbnail");
    setThumbnailModalOpen(true);
  };

  const handleOpenFabThumbnailModal = () => {
    setThumbnailType("aone");
    setThumbnailModalOpen(true);
  };

  const handleOpenYouTubeModal = () => {
    setIsYouTubeModalOpen(true);
  };

  const handleSocialShare = (platform: "facebook" | "whatsapp") => {
    setSelectedPlatform(platform);
    setSelectTemplateOpen(true);
  };

  const handleSelectTemplate = (template: ShareTemplate) => {
    setSelectedTemplate(template);
    setSocialModalOpen(true);
  };

  const handleSaveNetworkingData = (_data: unknown[]) => {
    void _data;
    setSnackbarMessage("Networking statistics saved.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleThumbnailSaved = (_url: string) => {
    void _url;
    setTimeout(() => {
      setThumbnailRefreshTrigger((prev) => prev + 1);
    }, 1500);
  };

  const handleYouTubeLinkSuccess = () => {
    fetchData();
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const clean = (val: string | undefined | null): string => {
    if (val === null || val === undefined || val === "N/A") {
      return "";
    }
    return val;
  };

  const deceasedName = [
    clean(stream?.booking.deceased[0]?.first_name),
    clean(stream?.booking.deceased[0]?.second_name),
    clean(stream?.booking.deceased[0]?.surname),
  ]
    .filter(Boolean)
    .join(" ");

  const streamData =
    stream && stream.booking
      ? {
          deceasedName,
          date: formatDateToLongString(stream.date),
          time: stream.start,
          serviceType: stream.booking.service,
          eventName: stream?.booking?.events?.[0]?.title || "",
          venueName:
            (stream.venue_parent
              ? `${stream.venue_parent.name} — ${stream.venue_name || ""}`
              : stream.venue_name || "") ||
            formatVenueName(stream?.service?.venue) ||
            formatVenueName(stream?.booking?.events?.[0]?.venue) ||
            "",
          address: [
            stream.address_one,
            stream.address_two,
            stream.suburb,
            stream.city,
          ]
            .map(clean)
            .filter(Boolean)
            .join(", "),
          website_stream_link: stream.website_stream_link || "",
          imageUrl: "",
        }
      : {
          deceasedName: "Unknown",
          date: "",
          eventName: "",
          time: "",
          serviceType: "",
          venueName: "N/A",
          address: "",
          website_stream_link: "",
          imageUrl: "",
        };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !stream) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error || "Stream not found"}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <StreamHeader
        stream={stream}
        onCreateThumbnail={handleOpenThumbnailModal}
        onCreateYouTubeLink={handleOpenYouTubeModal}
        onOpenChannelSelectModal={() => setChannelSelectOpen(true)}
        onSocialShare={handleSocialShare}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <EventDetailsCard stream={stream} />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <TimeAllocationCard timeData={timeData} />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ height: "100%", boxShadow: 0, p: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Graphics & Media
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between ",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  color: reduxTheme.primary_colors[500],
                  fontStyle: "italic",
                }}
              >
                Click here to change the thumbnail
              </Typography>
              <Tooltip title="Download Thumbnail">
                <IconButton
                  onClick={() => downloadFn?.()}
                  disabled={!downloadFn}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>

            <DynamicThumbnailCard
              streamId={streamId}
              streamData={stream}
              refreshTrigger={thumbnailRefreshTrigger}
              height={250}
              width={350}
              onReadyDownload={(fn) => setDownloadFn(() => fn)}
            />
            {stream?.booking?.service?.toLowerCase() === "funeral" && (
              <MemorialGraphicCard
                streamId={streamId}
                fullName={deceasedName}
                funeralStartTime={formatTime(stream.start)}
                birthDate={formatDate(
                  stream?.booking.deceased[0]?.date_of_birth,
                )}
                deathDate={formatDate(
                  stream?.booking.deceased[0]?.date_of_death,
                )}
                funeralDateText={formatDateToLongString(stream?.date)}
                venueText={`${stream?.address_one || ""}, ${stream?.city || ""}`}
                imageUrl={stream?.booking?.deceased[0]?.person_image || ""}
                patternSvgUrl={sharemessagegraphicpattern}
                livestreamedBy={muzukurulogo}
              />
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <CrewAssignmentTable
            crewMembersData={crewMembersData}
            streamId={streamId}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          {streamId && (
            <Box sx={{ mb: 2 }}>
              <FundraiserCard streamId={streamId} />
            </Box>
          )}
          <NetworkingStatisticsCard
            networkData={networkData}
            onOpenNetworking={() => setIsModalOpen(true)}
          />
          <StreamProgramCard streamId={streamId} />
          <NotesCard streamId={streamId} />
          <Paper sx={{ boxShadow: 0, p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Cost Analysis
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: reduxTheme.primary_colors[500] }}
              >
                Review the cost breakdown for this stream, or print it for
                sign-off.
              </Typography>
              <Button
                variant="contained"
                startIcon={<ReceiptLongOutlined />}
                onClick={() => setCostAnalysisOpen(true)}
                sx={{
                  backgroundColor: reduxTheme.primary_colors[400],
                  color: "#fff",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  ml: 2,
                  "&:hover": {
                    backgroundColor: reduxTheme.primary_colors[500],
                  },
                }}
              >
                View Cost Analysis
              </Button>
            </Box>
          </Paper>
          {stream?.booking?.service?.toLowerCase() === "funeral" && (
            <ServiceDeliveryNoteCard stream={stream} />
          )}
          {stream?.booking?.service?.toLowerCase() === "funeral" && (
            <MemoryBookAssignment
              streamId={stream.id}
              deceasedName={stream.title}
            />
          )}
        </Grid>
      </Grid>

      {streamData.serviceType !== "Church" &&
        streamData.serviceType !== "Conference" && (
          <Fab
            variant="extended"
            color="primary"
            aria-label="add"
            onClick={handleOpenFabThumbnailModal}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              "& .MuiFab-label": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          >
            Download A1
          </Fab>
        )}

      {/* Modals */}
      <YouTubeModal
        open={isYouTubeModalOpen}
        onClose={() => setIsYouTubeModalOpen(false)}
        streamData={stream}
        crewMembers={crewMembersData}
        onSuccess={handleYouTubeLinkSuccess}
        selectedChannel={selectedChannel}
      />
      <SelectYouTubeChannelModal
        open={channelSelectOpen}
        onClose={() => setChannelSelectOpen(false)}
        channels={channels}
        onSelect={(channel) => {
          setSelectedChannel(channel);
          setChannelSelectOpen(false);
          setIsYouTubeModalOpen(true);
        }}
      />

      <AddNetworkingStatisticsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveNetworkingData}
        streamId={Number(id)}
        refreshNetworkData={fetchNetworkData}
      />

      <SocialShareModal
        open={socialModalOpen}
        onClose={() => setSocialModalOpen(false)}
        streamData={streamData}
        platform={selectedPlatform}
        selectedTemplate={selectedTemplate}
      />

      <SelectTemplateModal
        open={selectTemplateOpen}
        onClose={() => setSelectTemplateOpen(false)}
        onSelectTemplate={handleSelectTemplate}
        serviceType={stream?.booking?.service}
      />

      <ThumbnailPreviewDialog
        open={thumbnailModalOpen}
        onClose={() => setThumbnailModalOpen(false)}
        thumbnailType={thumbnailType}
        streamData={{
          id: stream?.id?.toString() || "0",
          booking_id: stream?.booking?.id?.toString() || "0",
          bookingData: {
            booking_id: stream?.booking?.id?.toString() || "0",
            booking_type: stream?.booking?.booking_type as
              | "FUNERAL"
              | "CHURCH"
              | "FUNERAL_HOME",
            deceased_name: stream?.booking?.deceased?.[0]?.first_name || "",
            deceased_surname: stream?.booking?.deceased?.[0]?.surname || "",
            deceased_second_name:
              stream?.booking?.deceased?.[0]?.second_name || "",
            deceased_dob: stream?.booking?.deceased?.[0]?.date_of_birth || "",
            deceased_dod: stream?.booking?.deceased?.[0]?.date_of_death || "",
            deceased_photo_url:
              stream?.booking?.deceased?.[0]?.person_image || "",
            deceased_picture_url:
              stream?.booking?.deceased?.[0]?.person_image || "",
            deceased_ids: stream?.booking?.deceased?.map((d) => d.id) || [],
          },
          booking: stream?.booking
            ? {
                deceased: stream.booking.deceased?.map((d) => ({
                  id: d.id,
                  first_name: d.first_name,
                  second_name: d.second_name,
                  surname: d.surname,
                  date_of_birth: d.date_of_birth,
                  date_of_death: d.date_of_death,
                  gender: d.gender,
                  person_image: d.person_image || "",
                })),
              }
            : undefined,
          venue_name:
            stream?.booking?.events?.[0]?.venue?.name ||
            stream?.booking?.services?.[0]?.venue?.name ||
            "",
          start_time: stream.start,
          end_time: stream.end,
          special_notes: stream.service?.service_notes,
          website_stream_link: stream?.website_stream_link || "",
          date: stream?.date || "",
          location:
            stream?.service?.venue?.name ||
            stream?.booking?.services?.[0]?.venue?.name ||
            "N/A",
          address: `${stream?.address_one || ""}, ${stream?.city || ""}`,
          shoot_from: stream?.start || "",
          title: stream?.title || "",
        }}
        onSave={handleThumbnailSaved}
        onDownload={() => {
          console.log("Thumbnail downloaded successfully");
        }}
        refreshThumbnails={() => {
          console.log("Refreshing thumbnails");
        }}
        refreshStream={fetchData}
      />
      <Dialog
        open={costAnalysisOpen}
        onClose={() => setCostAnalysisOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <IconButton
          onClick={() => setCostAnalysisOpen(false)}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          {streamId && <CostAnalysis id={streamId} />}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity={snackbarSeverity}
          sx={{ width: "100%", color: reduxTheme.primary_colors[900] }}
          onClose={handleCloseSnackbar}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {(stream?.booking?.created_by || stream?.booking?.last_updated_by) && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            width: "100%",
            padding: "16px 24px",
            marginTop: "20px",
            backgroundColor: reduxTheme.primary_colors?.[900] || "#f8f8f8",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            borderRadius: "8px",
          }}
        >
          {stream?.booking?.created_by && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color={reduxTheme.neutral_colors?.[100]}
                sx={{ fontWeight: "medium" }}
              >
                Created By
              </Typography>

              <Typography
                variant="body2"
                color={reduxTheme.primary_colors?.[400] || "#fff"}
                sx={{ fontWeight: "bold" }}
              >
                {stream?.booking?.created_by?.first_name}{" "}
                {stream?.booking?.created_by?.last_name}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              color={reduxTheme.neutral_colors?.[100]}
              sx={{ fontWeight: "medium" }}
            >
              Last Updated By
            </Typography>

            {stream?.booking?.last_updated_by &&
            typeof stream?.booking?.last_updated_by !== "string" ? (
              <Typography
                variant="body2"
                color={reduxTheme.primary_colors?.[400] || "#fff"}
                sx={{ fontWeight: "bold" }}
              >
                {stream?.booking?.last_updated_by?.first_name}{" "}
                {stream?.booking?.last_updated_by?.last_name}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color={reduxTheme.primary_colors?.[400] || "#fff"}
                sx={{ fontWeight: "bold" }}
              >
                —
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ViewStream;
